import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackUserAction } from '../utils/analytics';
import { EXPLORE_PRIVACY_POLICY_URL } from '../config/legalUrls';
import AuthForm from '../components/auth/AuthForm';

const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const safeNextPath = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const path = raw.trim();
    if (!path.startsWith('/') || path.startsWith('//')) return null;
    return path;
  };

  const navigateAfterAuth = () => {
    const next = safeNextPath(searchParams.get('next') || '');
    navigate(next || '/');
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register({ reg_email: form.email, reg_password: form.password, reg_name: form.name });
      if (result.success) {
        trackUserAction.userRegistered();
        toast.success('Registered!');
        navigateAfterAuth();
      } else {
        toast.error(result.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await loginWithGoogle();
      if (result.success) {
        toast.success('Logged in with Google!');
        navigateAfterAuth();
      } else {
        toast.error(result.message || 'Google login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', padding: 20, background: 'var(--color-bg)' }}>
      <button className="btn" style={{ marginBottom: 24 }} onClick={() => navigate('/')}>← Back to Home</button>
      <div style={{ maxWidth: 400, margin: '40px auto', background: 'var(--color-bg-card)', borderRadius: 16, padding: 32, boxShadow: '0 4px 24px var(--color-shadow)' }}>
        <h1 style={{ color: 'var(--color-text)', fontSize: 28, fontWeight: 'bold', marginBottom: 16, textAlign: 'center' }}>
          {mode === 'login' ? 'Login' : 'Register'}
        </h1>

        <div
          role="note"
          aria-label="Adult account requirement"
          style={{
            marginBottom: 20,
            padding: '14px 16px',
            borderRadius: 12,
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            textAlign: 'left',
          }}
        >
          <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: 'var(--color-text)', lineHeight: 1.35, marginBottom: 8 }}>
            Adult accounts only
          </p>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--color-text)', opacity: 0.9 }}>
            Sign in or register using an email that belongs to a <strong>parent, guardian, school administrator, or educator</strong>.
            Do <strong>not</strong> use a child's email or set up an account for a child to use—Explore sign-in is meant for adults who supervise the experience.
          </p>
        </div>

        {mode === 'login' ? (
          <AuthForm
            onSuccess={() => { toast.success('Logged in!'); navigateAfterAuth(); }}
            onNavigateToSignUp={() => setMode('register')}
          />
        ) : (
          <>
            <form onSubmit={handleRegisterSubmit}>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Name"
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: 16, padding: '12px 16px', borderRadius: 12, border: '2px solid var(--color-accent)', fontSize: 16, background: 'var(--color-bg-card)', color: 'var(--color-text)' }}
                required
              />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email"
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: 16, padding: '12px 16px', borderRadius: 12, border: '2px solid var(--color-accent)', fontSize: 16, background: 'var(--color-bg-card)', color: 'var(--color-text)' }}
                required
              />
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="Password"
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: 24, padding: '12px 16px', borderRadius: 12, border: '2px solid var(--color-accent)', fontSize: 16, background: 'var(--color-bg-card)', color: 'var(--color-text)' }}
                required
              />
              <button
                className="btn"
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  marginBottom: 16,
                  background: 'var(--color-primary)',
                  color: 'var(--color-bg-card)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '14px 16px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  boxShadow: '0 2px 8px var(--color-shadow)',
                  transition: 'all 0.2s ease',
                }}
              >
                {loading ? '...' : 'Register'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ margin: '16px 0', color: 'var(--color-secondary)', fontSize: 14 }}>or</div>
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '2px solid var(--color-border)',
                  background: 'var(--color-bg-card)',
                  color: 'var(--color-text)',
                  fontSize: 16,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Signing in...' : 'Continue with Google'}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <span style={{ color: 'var(--color-secondary)' }}>
                Already have an account?{' '}
                <button
                  className="btn btn-outline"
                  style={{
                    fontSize: 14,
                    padding: '8px 16px',
                    background: 'var(--color-bg)',
                    color: 'var(--color-primary)',
                    border: '2px solid var(--color-primary)',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => setMode('login')}
                >
                  Login
                </button>
              </span>
            </div>
          </>
        )}

        <p style={{ textAlign: 'center', fontSize: 13, lineHeight: 1.45, color: 'var(--color-secondary)', marginTop: 20, marginBottom: 0 }}>
          <a
            href={EXPLORE_PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-primary)', fontWeight: 600 }}
          >
            Privacy policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
